<script lang="ts">
	//Design system components
	import { Button, ButtonGroup, ControlsContainer, toast } from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';
	//Configurators
	import { devices_config } from '@devices/index';
	//Hooks
	import { beforeUpdate } from 'svelte';
	//Stores
	import { createMemberStatusSnapshot, readDeviceStatus } from 'src/stores/device-runtime/status-selectors';
import { devices } from 'src/stores/lists/devices';
	//Helpers
	import {
		localGroupRequest,
		cloudGroupRequest,
		handleCloudLocalLists,
	} from './control_group_helpers';
	import type { Device } from '../../../types/Device';
	import { capFirstChar } from 'src/helpers/utilities';
	import { update_settings } from 'src/helpers/cloud_disabled_helpers';
	import { get_local_status } from 'src/services/local_requests';
	import { real_id } from 'src/helpers/interface_helpers';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let color_mode_lights; //devices are filtered devices without gen2 devices for now

	$: memberStatusStore = createMemberStatusSnapshot(color_mode_lights?.map((d) => d.id) ?? []);
	$: memberStatus = $memberStatusStore;
	$: firstLight = color_mode_lights?.[0];
	$: firstLightStatus = firstLight
		? memberStatus[real_id(firstLight.id)]?.lights?.[firstLight.channel]
		: undefined;

	let cloud = {} as Device,
		local = {} as Device,
		selected: number = firstLightStatus?.effect || 0,
		settingsEffects = [],
		loading = {};

	beforeUpdate(() => {
		if (firstLightStatus?.effect != selected) {
			selected = firstLightStatus?.effect || 0;
		}
		//1. first set effects
		//2. check out local, offline and cloud devices (it is one step for single device)
		//3. while we go through the cicle, check if the type is group
		//and if there is different effects, slice the array to the 3-rd index (they are equal for all devices)

		settingsEffects = devices_config[color_mode_lights[0]?.type]?.modes?.color?.effects || [];
		if (settingsEffects.length > 4) {
			settingsEffects = [...settingsEffects.slice(0, 4)];
		}
		if (Object.keys(loading).length < 1) {
			settingsEffects.forEach((effect) => {
				loading[effect.id] = false;
			});
		}

		let lists = handleCloudLocalLists(
			Array.from(Object.values(color_mode_lights).map((d: Device) => d.id)),
		);
		[cloud, local] = [lists.cloud, lists.local];
	});

	const sendRequest = async (effect) => {
		loading[effect] = true;
		if (Object.keys(cloud).length > 0)
			await cloudGroupRequest('device/light/settings/bulk_effect', Object.values(cloud), {
				effect,
			});
		if (Object.keys(local).length > 0) {
			for (const id in local) {
				const res = await localGroupRequest(local[id], { effect }, 'post', {
					type: 'color',
				});

				if (res.isok) {
					const status_req = await get_local_status($devices[id], $devices[id].ip);
					let newStatus = {
						...readDeviceStatus(real_id(id)),
						...status_req,
					};
					devices_status.update_device_status({
						id: real_id(id),
						msg: newStatus,
					});
					await update_settings($devices[id]);
				} else {
					toast.open({ type: 'danger', title: $format('error_global') });
				}
			}
		}
		loading[effect] = false;
	};
</script>

<ControlsContainer class="mb-xl" color={$showNewLayout ? 'color-0' : undefined}>
	<ButtonGroup
		aria-label={$format('light_effects', { default: 'Light Effects' })}
		id="buttongroup-0azcn4r"
		slot="first"
		orientation="vertical"
		class="width-full"
	>
		{#each settingsEffects as effect}
			<Button
				loading={loading[effect.id] === true}
				width="full"
				class={` ${selected === effect.id ? 'active' : ''}`}
				variant="secondary"
				on:click={async () => await sendRequest(effect.id)}
				>{$format(
					'select_title_light_effect_' + effect.label.toLowerCase().replace(/\s+/, '_'),
					{
						default: capFirstChar(effect.label),
					},
				).toUpperCase()}</Button
			>
		{/each}
	</ButtonGroup>
</ControlsContainer>
